import * as React from "react"
import { cn } from "../utils/cn"
import { cva, type VariantProps } from "class-variance-authority"

const statusIndicatorColors: Record<string, string> = {
    completed: "rgb(var(--ui-status-completed))",
    generating: "rgb(var(--ui-status-generating))",
    busy: "rgb(var(--ui-status-generating))",
    resuming: "rgb(var(--ui-status-generating))",
    pending: "rgb(var(--ui-status-pending))",
    started: "rgb(var(--ui-status-pending))",
    loading: "rgb(var(--ui-status-loading))",
    failed: "rgb(var(--ui-status-failed))",
    error: "rgb(var(--ui-status-failed))",
    default: "rgb(var(--ui-status-default))",
}

const statusIndicatorVariants = cva(
    "inline-flex flex-col items-center gap-2 z-10",
    {
        variants: {
            overlay: {
                true: "absolute bottom-3 left-3 rounded bg-black/65 px-3 py-2 text-white text-sm",
                false: "",
            },
            size: {
                default: "text-lg font-semibold italic",
                sm: "text-sm",
            },
        },
        defaultVariants: {
            overlay: false,
            size: "default",
        },
    }
)

// message mapping for different statuses (matching AOTD)
const getStatusMessage = (status: string): string => {
    switch (status) {
        case 'completed':
            return 'completed';
        case 'generating':
            return 'generating';
        case 'pending':
            return 'pending';
        case 'loading':
            return 'loading';
        case 'resuming':
            return 'resuming';
        case 'busy':
            return 'processing';
        case 'started':
            return 'initializing';
        case 'failed':
        case 'error':
            return 'failed';
        default:
            return status.toLowerCase();
    }
};

export interface StatusIndicatorProps
    extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof statusIndicatorVariants> {
    status: string | null
    message?: string
    showDot?: boolean
}

const StatusIndicator = React.forwardRef<HTMLDivElement, StatusIndicatorProps>(
    (
        { className, status, message, overlay, size, showDot = true, ...props },
        ref
    ) => {
        if (!status) return null

        const statusLower = status.toLowerCase()
        const color = statusIndicatorColors[statusLower] || statusIndicatorColors.default

        const displayMessage = message || getStatusMessage(status)
        const letters = displayMessage.split("")

        return (
            <div
                ref={ref}
                className={cn(statusIndicatorVariants({ overlay, size, className }))}
                style={
                    {
                        "--status-color": color,
                        "--base-gray-color": "rgb(136, 136, 136)",
                    } as React.CSSProperties
                }
                {...props}
            >
                <div className="flex items-center gap-2">
                    {showDot && (
                        <span className="relative flex h-4 w-4">
                            <span
                                className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"
                                style={{ backgroundColor: color }}
                            />
                            <span
                                className="relative inline-flex h-4 w-4 rounded-full"
                                style={{ backgroundColor: color }}
                            />
                        </span>
                    )}

                    <span className="inline-block tracking-[0.05em]">
                        {letters.map((letter, index) => (
                            <span
                                key={index}
                                className="inline-block animate-status-blink"
                                style={{
                                    animationDelay: `${index * 0.1}s`,
                                    backgroundImage: `linear-gradient(to left, ${color} 50%, var(--base-gray-color) 50%)`,
                                    backgroundSize: '200% 100%',
                                    backgroundPosition: '100% 0%',
                                    WebkitBackgroundClip: 'text',
                                    backgroundClip: 'text',
                                    color: 'transparent',
                                }}
                            >
                                {letter}
                            </span>
                        ))}
                    </span>
                </div>

                {message && (
                    <div className={cn(
                        "min-h-[1.8em] mt-2 flex items-center justify-center",
                        !overlay && "text-[#555]"
                    )}>
                        <span className="text-sm font-normal">{message}</span>
                    </div>
                )}
            </div>
        )
    }
)
StatusIndicator.displayName = "StatusIndicator"

export { StatusIndicator }










